import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import useDocumentMeta from '@/hooks/useDocumentMeta';

const CONTENT_SCOPES = [
  { icon: 'ri-film-line', title: '电影', desc: '最新院线大片、经典佳作与高分文艺片，覆盖动作、科幻、爱情、悬疑等多种类型。' },
  { icon: 'ri-tv-2-line', title: '电视剧', desc: '热播剧集、口碑长剧与年代剧，涵盖都市、古装、悬疑、家庭、历史等题材。' },
  { icon: 'ri-mic-line', title: '综艺', desc: '音乐、竞技、真人秀、访谈、生活等多元综艺内容，紧跟新季与热门话题。' },
];

const FEATURES = [
  { icon: 'ri-search-line', title: '精准搜索', desc: '支持片名、演员与关键词搜索，快速定位目标内容。' },
  { icon: 'ri-filter-3-line', title: '多维筛选', desc: '按地区、年份、类型自由组合筛选，排序更灵活。' },
  { icon: 'ri-fire-line', title: '热度推荐', desc: '依据播放热度与评分综合排序，优先发现值得一看的作品。' },
  { icon: 'ri-device-line', title: '全端适配', desc: '电脑与手机均自动适配，随时随地方便浏览。' },
];

export default function About() {
  useDocumentMeta({
    title: '关于我们 - 妖精视频',
    description:
      '妖精视频是一个面向广大影迷的综合影视内容展示与推荐平台，网罗最新电影、热门电视剧、精选综艺，帮助你更快发现想看的影视作品。',
    keywords: '妖精视频,关于我们,影视平台,影视资源,影视推荐',
  });

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 pt-6">
        <nav className="flex items-center gap-2 text-sm text-foreground-500">
          <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-900 font-medium">关于我们</span>
        </nav>
      </div>

      <section className="w-full px-4 md:px-8 mt-5">
        <div className="relative w-full rounded-lg overflow-hidden bg-primary-900 h-[300px] md:h-[380px]">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20cinema%20interior%20with%20rows%20of%20seats%20and%20a%20large%20illuminated%20screen%20glowing%20warm%20orange%2C%20deep%20navy%20shadow%20tones%2C%20elegant%20cinematic%20atmosphere%2C%20minimal%20abstract%20composition&width=1600&height=900&seq=yaojing-about-hero&orientation=landscape"
            alt="妖精视频平台氛围图"
            title="妖精视频 - 影视内容展示与推荐平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20"></div>
          <div className="relative z-10 h-full px-6 md:px-12 flex flex-col justify-center max-w-3xl">
            <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-background-50">关于妖精视频</h1>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-primary-100">
              妖精视频是一个面向广大影视爱好者和普通观众的综合影视内容展示与推荐平台。
              我们围绕最新电影、热门电视剧、精选综艺等多类别视频内容，为用户提供发现、筛选、
              浏览影视内容的一站式体验。
            </p>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-extrabold text-foreground-950">网站定位</h2>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-700">
              我们希望成为影视爱好者发现好内容的入口。区别于零散的资源站点，妖精视频注重
              <strong className="text-foreground-950">内容丰富、分类清晰与浏览流畅</strong>，
              通过统一的作品信息、多维筛选条件与热度推荐，让每一次"想看点什么"都能快速得到答案。
            </p>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-700">
              平台不提供真实播放与下载服务，所有内容展示以信息查询与观看决策支持为核心，
              相关"播放"入口仅作功能示意。
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {FEATURES.map((f) => (
              <div key={f.title} className="rounded-lg border border-background-200 bg-background-100 p-5">
                <span className="w-11 h-11 rounded-md bg-primary-500 flex items-center justify-center">
                  <i className={`${f.icon} text-xl text-background-50`}></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 pb-4">
        <h2 className="font-heading text-xl md:text-2xl font-extrabold text-foreground-950">内容范围</h2>
        <p className="mt-2 text-sm text-foreground-600">覆盖电影、电视剧、综艺等广泛影视资源</p>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
          {CONTENT_SCOPES.map((c) => (
            <div key={c.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-md bg-accent-500 flex items-center justify-center">
                  <i className={`${c.icon} text-xl text-background-50`}></i>
                </span>
                <h3 className="font-heading text-lg font-bold text-foreground-950">{c.title}</h3>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-12">
        <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-10">
          <h2 className="font-heading text-xl md:text-2xl font-extrabold text-foreground-950">联系我们</h2>
          <p className="mt-2 text-sm text-foreground-600">
            如果你对网站内容、合作或反馈有任何想法，欢迎随时联系我们。
          </p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-md bg-secondary-100 flex items-center justify-center">
                <i className="ri-mail-line text-xl text-secondary-700"></i>
              </span>
              <div>
                <div className="text-xs text-foreground-500">反馈邮箱</div>
                <div className="text-sm font-medium text-foreground-900">hello@yaojing-video.example</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-md bg-secondary-100 flex items-center justify-center">
                <i className="ri-customer-service-2-line text-xl text-secondary-700"></i>
              </span>
              <div>
                <div className="text-xs text-foreground-500">客服热线</div>
                <div className="text-sm font-medium text-foreground-900">400-000-0000</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-md bg-secondary-100 flex items-center justify-center">
                <i className="ri-map-pin-line text-xl text-secondary-700"></i>
              </span>
              <div>
                <div className="text-xs text-foreground-500">办公地址</div>
                <div className="text-sm font-medium text-foreground-900">中国 · 上海市</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 pb-12">
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
          <h2 className="font-heading text-lg font-bold text-foreground-950">版权与免责声明</h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            © 2026 妖精视频 · 版权所有。本站所展示的影视名称、简介、评分及图片均为演示用虚构示例，
            仅用于产品功能与界面展示，不构成任何真实影视作品的宣传或推荐。本站不提供视频播放与下载服务，
            相关入口仅为交互示意。
          </p>
        </div>
      </section>
    </SiteLayout>
  );
}